import { usePublicLanguage } from '../public-site/PublicLanguage';
import React from 'react';
import { LOGIN_URL, SIGNUP_URL, STUDY_URL } from '../public-site/routes';
import { PublicHeader, PublicFooter } from '../public-site/PublicLayout';
import ArticleCards from '../public-site/ArticleCards';
import { BlogSummary } from '../public-site/blog';

export const HOME_TITLE = 'Revelationary | Read the Bible. Study together.';
export const HOME_DESCRIPTION =
  'Read the Bible for free, explore biblical Hebrew and discuss scripture. Create a free account to join the conversation. No subscription needed to comment.';

export interface LandingPageProps {
  illustration: string;
  illustrationSrcSet?: string;
  logo: string;
  hasReadingHistory?: boolean;
  dark?: boolean;
  onToggleTheme?: () => void;
  articles?: BlogSummary[];
}

/** Shared by the browser and Bun HTML renderer. No account data or API calls. */
export default function LandingPage({
  illustration,
  illustrationSrcSet,
  logo,
  hasReadingHistory = false,
  dark = false,
  onToggleTheme,
  articles = [],
}: LandingPageProps) {
  const { t, href } = usePublicLanguage();
  return (
    <div className="public-home" data-theme={dark ? 'dark' : 'light'}>
      <PublicHeader logo={logo} dark={dark} onToggleTheme={onToggleTheme} />

      <main id="main">
        <section className="home-hero home-width" aria-labelledby="home-title">
          <div className="home-hero-copy">
            <p className="home-eyebrow">{t('An open Bible. A shared journey.')}</p>
            <h1 id="home-title">
              {t('Read the Bible.')}
              <br />
              <span>{t('Study together.')}</span>
            </h1>
            <p className="home-intro">
              {t(
                'Explore scripture, discover its language and bring your questions to the conversation.',
              )}
            </p>
            <div className="home-actions">
              <a
                className="home-button"
                href={hasReadingHistory ? `${STUDY_URL}#/?resume=1` : STUDY_URL}
              >
                {t(hasReadingHistory ? 'Continue reading' : 'Open the Bible — free')}
              </a>
              <a className="home-text-link" href={SIGNUP_URL}>
                {t('Create a free account')}
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
          <div className="home-art">
            <img
              src={illustration}
              srcSet={illustrationSrcSet}
              sizes="(max-width: 620px) calc(100vw - 40px), (max-width: 900px) calc(64.286vw - 46.286px), (max-width: 1376px) calc(64.286vw - 77.143px), 808px"
              // React 18 forwards this native attribute using its lowercase spelling.
              {...{ fetchpriority: 'high' }}
              alt={t('An illustrated landscape of mountains, trees and flowing water')}
              width="1600"
              height="885"
            />
          </div>
        </section>

        <section className="home-explore home-width" id="explore" aria-labelledby="explore-title">
          <div className="home-section-heading">
            <h2 id="explore-title">
              {t('Follow your curiosity.')}
              <br />
              {t('Stay close to the text.')}
            </h2>
            <p>{t('A familiar passage can be the beginning of a new question.')}</p>
          </div>
          <div className="home-study-layout">
            <figure className="home-passage">
              <figcaption>Genesis 1:1 · King James Version</figcaption>
              <blockquote lang="en">
                In the beginning God created the heaven and the earth.
              </blockquote>
              <p lang="he" dir="rtl" className="home-hebrew">
                בְּרֵאשִׁית
              </p>
              <p>
                {t(
                  'The opening word of Genesis. Explore Hebrew words alongside the passage in the reader.',
                )}
              </p>
              <a className="home-text-link" href={STUDY_URL}>
                {t('Explore the Bible')}
                <span aria-hidden="true">↗</span>
              </a>
            </figure>
            <div className="home-features">
              <article>
                <h3>{t('Read in your language')}</h3>
                <p>
                  {t(
                    'Move between Bible translations, search for a phrase and keep the passage in view as you study.',
                  )}
                </p>
              </article>
              <article>
                <h3>{t('Look into the words')}</h3>
                <p>
                  {t(
                    'Open the Hebrew explorer to investigate the language behind Old Testament scripture.',
                  )}
                </p>
              </article>
              <article>
                <h3>{t('Bring a question')}</h3>
                <p>
                  {t(
                    'Discuss a verse, respond to another perspective and connect your thoughts to the text.',
                  )}
                </p>
                <p className="home-small">{t('Comments and replies are free with an account.')}</p>
              </article>
            </div>
          </div>
        </section>

        <section className="home-community home-width" aria-labelledby="community-title">
          <div>
            <h2 id="community-title">
              {t('There is room')}
              <br />
              {t('for your questions.')}
            </h2>
            <p>
              {t(
                'What does this passage mean? How does it relate to another verse, a scientific idea or something in the news?',
              )}
            </p>
            <p>
              {t(
                'Start with the scripture. Share your sources. Make room for another interpretation.',
              )}
            </p>
          </div>
          <aside className="home-invitation">
            <h3>{t('Join the conversation')}</h3>
            <p>
              {t(
                'Create a free account with your email to add comments and replies. No subscription or payment card required.',
              )}
            </p>
            <a className="home-button" href={SIGNUP_URL}>
              {t('Join free to comment')}
            </a>
            <p className="home-small">
              {t('Already have an account?')}
              <a href={LOGIN_URL}>{t('Sign in')}</a>
            </p>
          </aside>
        </section>

        <section className="home-articles home-width" aria-labelledby="articles-title">
          <div className="home-section-heading">
            <h2 id="articles-title">{t('Keep exploring.')}</h2>
            <p>{t('Ideas and questions to take into your next Bible study.')}</p>
          </div>
          {articles.length > 0 && <ArticleCards posts={articles.slice(0, 3)} />}
          <a className="home-text-link" href={href('/blog/')}>
            {t('Explore the blog')}
            <span aria-hidden="true">↗</span>
          </a>
        </section>

        <section className="home-faq home-width" aria-labelledby="faq-title">
          <h2 id="faq-title">{t('Before you begin')}</h2>
          <div>
            <details>
              <summary>{t('Can I read without an account?')}</summary>
              <p>{t('Yes. Open the Bible and start reading or searching without signing up.')}</p>
            </details>
            <details>
              <summary>{t('Do I need a subscription to comment?')}</summary>
              <p>
                {t(
                  'No. Commenting and replying are free. Create an account so your contributions belong to you.',
                )}
              </p>
            </details>
            <details>
              <summary>{t('Where do I find the Hebrew tools?')}</summary>
              <p>
                {t(
                  'Open the Bible reader and select an Old Testament passage. Use the Hebrew explorer to look into its words.',
                )}
              </p>
            </details>
            <a className="home-text-link" href={href('/faq/')}>
              {t('More about Revelationary')}
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
